import { useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { kstHm, type Attendance, type AwayStatus, type CalendarItem, type OvertimeRecord } from '@dxtech/shared';
import { overtimeApi } from '../api/overtime.js';
import { OvertimeBadge, actualSpan, planSpan } from './Overtime.js';
import { api } from '../api/client.js';
import { dateForOffset, useApp } from '../state/app.js';
import { Avatar, Spinner } from '../ui/common.js';
import { StatusChip, type WorkStatus } from '../ui/status.js';
import { IconCalSmall, IconDownload } from '../ui/icons.js';
import { DateNav } from '../ui/DateNav.js';
import { kstHHmm, workedLabel, attStatus, boardStatus } from '../lib/format.js';
import { AttendanceCorrectModal } from '../modals/AttendanceCorrectModal.js';

interface Row {
  upn: string; name: string; dept: string;
  in: string; out: string; worked: string; status: WorkStatus; schedule: CalendarItem[];
  away?: AwayStatus | null; // 오늘이면 진행 중 또는 곧 시작할 자리비움(서버가 골라 줌)
  ot?: OvertimeRecord; // 그날 야근(신청·진행·완료)
  checkInAt?: string | null; checkOutAt?: string | null;
}

/** 일정 1건 표시 — 종일이면 '종일', 아니면 시작~종료 시각 */
const schedTime = (s: CalendarItem): string => (s.isAllDay ? '종일' : `${kstHHmm(s.start)}~${kstHHmm(s.end)}`);

// 근무현황 — 전 임직원 열람(상태·일정). 출근·퇴근·근무시간·CSV 는 CEO·임원·관리자(canViewAdmin), 근태 보정은 Admin.
export function Admin(): JSX.Element {
  const { dateOffset, flash, me } = useApp();
  const [dept, setDept] = useState('전체');
  const [editing, setEditing] = useState<Row | null>(null);
  const isAdmin = me.roles.includes('Admin');
  const canSeeTimes = me.permissions.canViewAdmin;
  const date = dateForOffset(dateOffset).date;

  const board = useQuery({ queryKey: ['board', date], queryFn: () => api.board(date) });
  const att = useQuery({ queryKey: ['attendance', 'admin', date], queryFn: () => api.attendanceAdmin(date, date), enabled: canSeeTimes });
  const ots = useQuery({ queryKey: ['overtime', 'date', date], queryFn: () => overtimeApi.byDate(date), refetchInterval: 60_000 });
  const otMap = useMemo(() => new Map((ots.data?.items ?? []).map((r) => [r.upn.toLowerCase(), r])), [ots.data]);

  const entries = board.data?.entries ?? [];
  const attMap = useMemo(() => {
    const m = new Map<string, Attendance>();
    for (const a of att.data?.items ?? []) m.set(a.empUpn, a);
    return m;
  }, [att.data]);

  const deptOptions = useMemo(
    () => ['전체', ...Array.from(new Set(entries.map((e) => e.dept).filter(Boolean)))],
    [entries],
  );

  const rows: Row[] = entries
    .filter((e) => dept === '전체' || e.dept === dept)
    .map((e) => {
      const a = canSeeTimes ? attMap.get(e.upn) ?? null : null;
      const bs = boardStatus(e);
      // 출퇴근 기록을 볼 수 있으면 away/vacation/trip/exempt 외에는 출퇴근 기반, 아니면 보드 상태 그대로
      const status: WorkStatus = !canSeeTimes || bs === 'away' || bs === 'vacation' || bs === 'trip' || bs === 'exempt' ? bs : attStatus(a);
      return {
        upn: e.upn, name: e.name, dept: e.dept, schedule: e.schedule ?? [], away: e.away, ot: otMap.get(e.upn.toLowerCase()),
        in: kstHHmm(a?.checkInAt), out: kstHHmm(a?.checkOutAt), worked: workedLabel(a), status,
        checkInAt: a?.checkInAt ?? null, checkOutAt: a?.checkOutAt ?? null,
      };
    });

  const exportCsv = (): void => {
    const head = ['이름', '부서', '상태', '일정', '야근 예정', '야근 실제', '출근', '퇴근', '근무시간'];
    const lines = [head, ...rows.map((r) => [r.name, r.dept, statusLabel(r.status), r.schedule.map((s) => `${schedTime(s)} ${s.subject ?? ''}`.trim()).join(' / '), r.ot ? planSpan(r.ot) : '', r.ot ? actualSpan(r.ot) : '', r.in, r.out, r.worked])];
    const csv = '﻿' + lines.map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `근무현황_${date}.csv`; a.click();
    URL.revokeObjectURL(url);
    flash('CSV 파일을 내보냈습니다');
  };

  const loading = board.isLoading || (canSeeTimes && att.isLoading);
  const GRID = canSeeTimes ? '1.3fr 200px 2fr 70px 70px 92px' : '1.3fr 220px 3fr';
  const head = (t: string) => <div style={{ padding: '10px 12px' }}>{t}</div>;
  const counts = rows.reduce<Partial<Record<WorkStatus, number>>>((m, r) => ({ ...m, [r.status]: (m[r.status] ?? 0) + 1 }), {});

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'dxfade .3s ease' }}>
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>근무 현황</div>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>
            {(['working', 'trip', 'away', 'vacation', 'off', 'absent'] as WorkStatus[]).filter((s) => counts[s]).map((s) => `${statusLabel(s)} ${counts[s]}`).join(' · ')}
          </span>
          {isAdmin && <span style={{ fontSize: 11.5, color: '#9CA3AF', fontWeight: 500 }}>행 클릭 시 근태 보정</span>}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center', flexWrap: 'wrap' }}>
            <DateNav />
            <select className="dx-select" value={dept} onChange={(e) => setDept(e.target.value)} aria-label="부서">
              {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            {canSeeTimes && (
              <button className="dx-btn dx-btn-ghost" style={{ fontSize: 13, padding: '7px 13px' }} onClick={exportCsv} disabled={rows.length === 0}>
                <IconDownload size={15} />CSV 내보내기
              </button>
            )}
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: canSeeTimes ? 760 : 520 }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, background: '#F9FAFB', borderBottom: '1px solid #EDEFF2', fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
                <div style={{ padding: '10px 16px' }}>이름</div>
                {head('상태')}{head('일정')}
                {canSeeTimes && <>{head('출근')}{head('퇴근')}{head('근무시간')}</>}
              </div>
              {rows.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>표시할 임직원이 없습니다.</div>
              ) : rows.map((r, i) => (
                <div key={r.upn}
                  onClick={isAdmin ? () => setEditing(r) : undefined}
                  className={isAdmin ? 'dx-att-row' : undefined}
                  title={isAdmin ? '근태 보정' : undefined}
                  style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid #F3F4F6', fontSize: 13, alignItems: 'center', background: i % 2 === 0 ? '#fff' : '#FAFBFC', cursor: isAdmin ? 'pointer' : 'default' }}>
                  <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar name={r.name} index={i} size={30} upn={r.upn} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, ...ellipsis }}>{r.name}</div>
                      <div style={{ fontSize: 11.5, color: '#9CA3AF', ...ellipsis }}>{r.dept}</div>
                    </div>
                  </div>
                  {/* 상태 + 자리비움·야근 뱃지 */}
                  <div style={{ padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <StatusChip s={r.status} />
                    {r.away && r.status !== 'away' && (
                      <span title={r.away.reason ?? '자리비움'} style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#F3E8FF', color: '#7E22CE', whiteSpace: 'nowrap' }}>
                        자리비움 {kstHm(r.away.startTime)}~{kstHm(r.away.endTime)}
                      </span>
                    )}
                    {r.ot && <OvertimeBadge status={r.ot.status} />}
                  </div>
                  {/* 일정 — 최대 2건 표시, 나머지는 +N */}
                  <div style={{ padding: '8px 12px', minWidth: 0 }}>
                    {/* 야근 상세 — 예정과 실제 시작·종료 */}
                    {r.ot && (
                      <div style={{ fontSize: 12.5, lineHeight: 1.6, color: '#4B5563' }} title={r.ot.reason}>
                        <span style={{ color: '#9CA3AF' }}>야근 예정</span> <span className="tnum">{planSpan(r.ot)}</span>
                        {r.ot.actualStart && <span style={{ marginLeft: 10 }}><span style={{ color: '#9CA3AF' }}>실제</span> <span className="tnum">{actualSpan(r.ot)}{r.ot.actualEnd ? '' : ' 진행 중'}</span></span>}
                      </div>
                    )}
                    {r.schedule.length === 0 ? (r.ot ? null : <span style={{ color: '#D1D5DB' }}>일정 없음</span>) : (
                      <>
                        {r.schedule.slice(0, 2).map((s, j) => (
                          <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4B5563', minWidth: 0, fontSize: 12.5, lineHeight: 1.6 }}>
                            <IconCalSmall size={12} />
                            <span className="tnum" style={{ color: '#9CA3AF', flexShrink: 0 }}>{schedTime(s)}</span>
                            <span style={ellipsis} title={s.subject}>{s.subject || '(제목 비공개)'}</span>
                          </div>
                        ))}
                        {r.schedule.length > 2 && <div style={{ fontSize: 11.5, color: '#2563EB' }}>+{r.schedule.length - 2}건</div>}
                      </>
                    )}
                  </div>
                  {canSeeTimes && (
                    <>
                      <div className="tnum" style={{ padding: '10px 12px', color: r.in === '—' ? '#D1D5DB' : '#1F2937' }}>{r.in}</div>
                      <div className="tnum" style={{ padding: '10px 12px', color: r.out === '—' ? '#D1D5DB' : '#1F2937' }}>{r.out}</div>
                      <div style={{ padding: '10px 12px', color: '#6B7280' }}>{r.worked}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <AttendanceCorrectModal
          upn={editing.upn} name={editing.name} date={date}
          checkInAt={editing.checkInAt} checkOutAt={editing.checkOutAt}
          onClose={() => setEditing(null)}
          onDone={(msg) => flash(msg)}
        />
      )}
    </div>
  );
}

const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;
const LABELS: Record<WorkStatus, string> = { working: '근무중', off: '퇴근', vacation: '휴가', away: '자리비움', absent: '미출근', trip: '출장중', exempt: '관리 예외' };
function statusLabel(s: WorkStatus): string { return LABELS[s]; }
